// assets/es-vehicle-popover.js
// New feature, 2026-07-27 (Jeff): clicking "Change" on the persistent "Your Vehicle" bar
// (assets/vehicle-memory-bar.js) used to clear the saved vehicle and hard-navigate to the
// homepage, since that was the only place with a real Make/Model/Year selector. Jeff wants that
// to happen in place instead -- a small popover right under the bar, on whatever page the
// shopper is already on. This module is that popover: a lightweight, page-agnostic version of
// the homepage's own selector (assets/ymm-filter.js), sharing the exact same fitment data source,
// tag-alias maps (assets/vehicle-tag-maps.js), and candidate-path resolution logic, so a
// selection made here lands on precisely the same collection page picking it from the homepage
// would. Exposed globally as window.ESVehiclePopover so both the vehicle bar's "Change" link and
// a new always-visible header icon (for shoppers who haven't picked a vehicle yet at all) can
// open the exact same popover instance.
(function () {
  function esR8Labels(data) {
    if (!data || !data.Audi || typeof data.Audi !== 'object') return data;
    Object.keys(data.Audi).filter(function(model) { return /^R8(?:\b|$)/i.test(model); }).forEach(function(model) {
      var entries = data.Audi[model];
      if (!Array.isArray(entries)) return;
      entries.forEach(function(entry) {
        if (!entry || typeof entry.label !== 'string') return;
        entry.label = entry.label.replace(/\b(?:Type\s+)?42\b/gi, 'Gen 1').replace(/\b(?:Type\s+)?4S\b/gi, 'Gen 2');
      });
    });
    return data;
  }

  var FITMENT_URL = 'https://worker1.speeddemon981.workers.dev/fitment-live?auth=eurospeed';
  var POPOVER_ID = 'es-vehicle-popover';

  var state = { fitment: null, fetchPromise: null, yearEntries: [], open: false };

  function VT() { return window.ESVehicleTags || { MAKE_TAGS: {}, MODEL_TAGS: {}, pickAlias: function (m, k) { return k; } }; }

  // Same candidate-degradation logic as ymm-filter.js's go()/collectionPath()/pathExists() --
  // duplicated rather than imported since ymm-filter.js is scoped to a specific homepage section
  // and isn't loaded on most pages this popover needs to work from.
  var GEN_QUALIFIER_RE = /\s+(facelift|pre-facelift|lci)$/i;
  function bareParentTag(t) { return t.replace(GEN_QUALIFIER_RE, ''); }
  // Bug fixed 2026-07-29 (same root cause as assets/ymm-filter.js's tagTermFor): recursing as
  // tagTermFor(bare) + ' OR ' + tagTermFor(t) called itself again with the SAME qualified `t`,
  // so `bare !== t` never became false and it blew the call stack on every qualified tag
  // (any "X Facelift"/"X Pre-Facelift"/"X LCI"), aborting go() with no navigation at all.
  function tagTerm(t) { return /[\s.]/.test(t) ? 'tag:"' + t + '"' : 'tag:' + t; }
  function tagTermFor(t) {
    var bare = bareParentTag(t);
    if (bare !== t) return '(' + tagTerm(bare) + ' OR ' + tagTerm(t) + ')';
    return tagTerm(t);
  }
  function searchPath(tags) {
    var q = tags.filter(Boolean).map(tagTermFor).join(' ');
    return '/search?q=' + encodeURIComponent(q) + '&type=product';
  }
  function hasDotTag(tags) { return tags.some(function (t) { return t && t.indexOf('.') !== -1; }); }
  function hasQualifiedGenTag(tags) { return tags.some(function (t) { return t && bareParentTag(t) !== t; }); }
  function collectionPath(tags) {
    var clean = tags.filter(Boolean);
    if (hasDotTag(clean) || hasQualifiedGenTag(clean)) return searchPath(clean);
    return clean.length ? '/collections/all/' + clean.map(encodeURIComponent).join('+') : '/collections/all';
  }
  async function pathExists(path) {
    try {
      var r = await fetch(path, { cache: 'no-store' });
      if (!r.ok) return false;
      var doc = new DOMParser().parseFromString(await r.text(), 'text/html');
      return doc.querySelector('#es-grid .product-item') !== null;
    } catch (e) { return true; }
  }

  function fetchFitment() {
    if (state.fetchPromise) return state.fetchPromise;
    state.fetchPromise = fetch(FITMENT_URL, { cache: 'no-store' })
      .then(function (r) { return r.ok ? r.json() : {}; })
      .then(function (data) { state.fitment = esR8Labels((data && typeof data === 'object') ? data : {}); return state.fitment; })
      .catch(function () { state.fitment = {}; return state.fitment; });
    return state.fetchPromise;
  }

  function ensureStyle() {
    if (document.getElementById(POPOVER_ID + '-style')) return;
    var style = document.createElement('style');
    style.id = POPOVER_ID + '-style';
    style.textContent =
      '#' + POPOVER_ID + ' { position: fixed; z-index: 1000; display: none; }' +
      '#' + POPOVER_ID + '.es-vp--open { display: block; }' +
      '#' + POPOVER_ID + ' .es-vp-card {' +
        'width: 320px; max-width: calc(100vw - 24px); background: #fff; border: 1px solid #dedede;' +
        'border-radius: 14px; box-shadow: 0 16px 40px rgba(17,24,39,.16); padding: 18px; }' +
      '#' + POPOVER_ID + ' .es-vp-title {' +
        'font-size: 12px; letter-spacing: 1.4px; text-transform: uppercase; color: #555;' +
        'font-weight: 700; margin: 0 0 12px; }' +
      '#' + POPOVER_ID + ' select {' +
        'display: block; width: 100%; height: 44px; padding: 0 12px; margin-bottom: 10px;' +
        'border: 1px solid #dbe1e8; border-radius: 10px; background: #fff; font-size: 14px;' +
        'color: #111827; appearance: none; }' +
      '#' + POPOVER_ID + ' select:disabled { background: #f7f7f7; color: #9ca3af; }' +
      '#' + POPOVER_ID + ' .es-vp-go {' +
        'width: 100%; height: 44px; border: none; border-radius: 10px; margin-top: 4px;' +
        'background: var(--engoc-primary-color, #10a8ee); color: #fff; font-weight: 700;' +
        'font-size: 13.5px; letter-spacing: .4px; cursor: pointer; transition: filter .15s ease; }' +
      '#' + POPOVER_ID + ' .es-vp-go:hover { filter: brightness(0.92); }' +
      '#' + POPOVER_ID + ' .es-vp-go:disabled { opacity: .5; cursor: default; filter: none; }' +
      '#' + POPOVER_ID + ' .es-vp-status { font-size: 12.5px; color: #666; margin: 8px 0 0; text-align: center; }';
    document.head.appendChild(style);
  }

  function build() {
    if (document.getElementById(POPOVER_ID)) return document.getElementById(POPOVER_ID);
    ensureStyle();
    var wrap = document.createElement('div');
    wrap.id = POPOVER_ID;
    wrap.innerHTML =
      '<div class="es-vp-card">' +
        '<p class="es-vp-title">Choose Your Vehicle</p>' +
        '<select id="es-vp-make"><option value="">Select Make…</option></select>' +
        '<select id="es-vp-model" disabled><option value="">Select Model…</option></select>' +
        '<select id="es-vp-year" disabled><option value="">Select Year…</option></select>' +
        '<button type="button" class="es-vp-go" id="es-vp-go" disabled>Update Vehicle</button>' +
        '<p class="es-vp-status" id="es-vp-status"></p>' +
      '</div>';
    document.body.appendChild(wrap);

    var makeSel = wrap.querySelector('#es-vp-make');
    var modelSel = wrap.querySelector('#es-vp-model');
    var yearSel = wrap.querySelector('#es-vp-year');
    var goBtn = wrap.querySelector('#es-vp-go');
    var status = wrap.querySelector('#es-vp-status');

    function populateMakes(fit) {
      makeSel.innerHTML = '<option value="">Select Make…</option>';
      var makes = Object.keys(fit).filter(function (m) { return fit[m] && Object.keys(fit[m]).length > 0; });
      var vt = VT();
      if (vt.sortMakes) makes = vt.sortMakes(makes); // European-first order, shared with ymm-filter.js
      makes.forEach(function (make) {
        var opt = document.createElement('option');
        opt.value = make; opt.textContent = make;
        makeSel.appendChild(opt);
      });
    }

    function setModels(make) {
      modelSel.innerHTML = '<option value="">Select Model…</option>';
      yearSel.innerHTML = '<option value="">Select Year…</option>';
      yearSel.disabled = true;
      state.yearEntries = [];
      goBtn.disabled = true;
      var fit = state.fitment || {};
      if (!make || !fit[make]) { modelSel.disabled = true; return; }
      var models = Object.keys(fit[make] || {});
      if (!models.length) { modelSel.disabled = true; return; }
      models.forEach(function (model) {
        var opt = document.createElement('option');
        opt.value = model; opt.textContent = model;
        modelSel.appendChild(opt);
      });
      modelSel.disabled = false;
    }

    function setYears(make, model) {
      yearSel.innerHTML = '<option value="">Select Year…</option>';
      state.yearEntries = [];
      goBtn.disabled = false; // make+model alone is enough to update the vehicle
      var fit = state.fitment || {};
      if (!make || !model || !fit[make] || !fit[make][model]) { yearSel.disabled = true; return; }
      state.yearEntries = fit[make][model] || [];
      if (!state.yearEntries.length) { yearSel.disabled = true; return; }
      state.yearEntries.forEach(function (entry, i) {
        var opt = document.createElement('option');
        opt.value = String(i);
        opt.textContent = entry && entry.label ? entry.label : String(entry);
        yearSel.appendChild(opt);
      });
      yearSel.disabled = false;
    }

    async function go() {
      var mk = makeSel.value || '';
      var md = modelSel.value || '';
      if (!mk) return;
      goBtn.disabled = true;
      status.textContent = 'Finding your vehicle…';

      var yrEntry = (yearSel.value !== '' && state.yearEntries[Number(yearSel.value)]) ? state.yearEntries[Number(yearSel.value)] : null;
      var vt = VT();
      var mkTag = mk ? vt.pickAlias(vt.MAKE_TAGS, mk) : '';
      var mdTag = md ? vt.pickAlias(vt.MODEL_TAGS, md) : '';
      var yrTags = (yrEntry && yrEntry.searchTags && yrEntry.searchTags.length) ? yrEntry.searchTags : [];

      var candidates = [];
      if (mkTag && mdTag) yrTags.forEach(function (t) { candidates.push([mkTag, mdTag, t]); });
      if (mkTag && mdTag) candidates.push([mkTag, mdTag]);
      if (mkTag) candidates.push([mkTag]);
      candidates.push([]);

      var targetTags = candidates[candidates.length - 1];
      var targetPath = collectionPath(targetTags);
      for (var i = 0; i < candidates.length; i++) {
        var path = collectionPath(candidates[i]);
        if (await pathExists(path)) { targetTags = candidates[i]; targetPath = path; break; }
      }

      if (window.ESVehicleMemory && mk && md) {
        var label = mk + ' ' + md + (yrEntry && yrEntry.label ? ' ' + yrEntry.label : '');
        window.ESVehicleMemory.save(label, targetPath, targetTags);
      }
      // Widened past what was chosen? (gen -> model, model -> make). Tell the landing page.
      var wantLevel = (mk && md && yrTags.length) ? 3 : (mk && md ? 2 : (mk ? 1 : 0));
      if (window.ESVehicleMemory && window.ESVehicleMemory.noteWiden && targetTags.length < wantLevel) {
        var genTxt = (wantLevel === 3 && yrEntry && yrEntry.label) ? ' ' + String(yrEntry.label).split(' — ')[0] : '';
        var got = targetTags.length >= 2 ? mk + ' ' + md : (targetTags.length === 1 ? mk : '');
        window.ESVehicleMemory.noteWiden({ want: (mk + ' ' + md).trim() + genTxt, got: got, path: targetPath });
      }
      window.location.href = targetPath;
    }

    makeSel.addEventListener('change', function () { setModels(this.value); });
    modelSel.addEventListener('change', function () { setYears(makeSel.value, this.value); });
    [makeSel, modelSel, yearSel].forEach(function (sel) {
      sel.addEventListener('keydown', function (e) { if (e.key === 'Enter' && !goBtn.disabled) go(); });
    });
    goBtn.addEventListener('click', go);

    wrap._populateMakes = populateMakes;
    return wrap;
  }

  function positionUnder(anchorEl, wrap) {
    var rect = anchorEl.getBoundingClientRect();
    var card = wrap.querySelector('.es-vp-card');
    var cardWidth = Math.min(320, window.innerWidth - 24);
    var left = rect.left + rect.width / 2 - cardWidth / 2;
    left = Math.max(12, Math.min(left, window.innerWidth - cardWidth - 12));
    wrap.style.top = (rect.bottom + 10) + 'px';
    wrap.style.left = left + 'px';
  }

  function close() {
    var wrap = document.getElementById(POPOVER_ID);
    if (wrap) wrap.classList.remove('es-vp--open');
    state.open = false;
    document.removeEventListener('click', onOutsideClick, true);
  }

  function onOutsideClick(e) {
    var wrap = document.getElementById(POPOVER_ID);
    if (!wrap) return;
    if (wrap.contains(e.target)) return;
    close();
  }

  async function open(anchorEl) {
    var wrap = build();
    positionUnder(anchorEl, wrap);
    wrap.classList.add('es-vp--open');
    state.open = true;
    document.addEventListener('click', onOutsideClick, true);

    var fit = await fetchFitment();
    wrap._populateMakes(fit);
  }

  window.ESVehiclePopover = {
    toggle: function (anchorEl) {
      var wrap = document.getElementById(POPOVER_ID);
      if (wrap && wrap.classList.contains('es-vp--open')) { close(); return; }
      open(anchorEl);
    },
    close: close
  };

  window.addEventListener('resize', function () {
    var wrap = document.getElementById(POPOVER_ID);
    if (wrap && wrap.classList.contains('es-vp--open')) {
      // Anchor is re-derived on next open rather than tracked continuously -- simplest safe
      // behavior is just to close on resize rather than risk a stale/offscreen position.
      close();
    }
  });

  // The always-visible header icon (snippets/header-v6.liquid, #es-header-vehicle-select) --
  // works whether or not a vehicle is already selected, since it's independent of the "Your
  // Vehicle" bar (which only renders once a vehicle exists).
  function bindHeaderIcon() {
    var btn = document.getElementById('es-header-vehicle-select');
    if (!btn || btn.__esVpBound) return;
    btn.__esVpBound = true;
    btn.addEventListener('click', function () { window.ESVehiclePopover.toggle(btn); });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindHeaderIcon);
  } else {
    bindHeaderIcon();
  }
})();
